import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Layout } from "./components/Layout";
import { BirthdayPopup } from "./components/BirthdayPopup";
import { Toaster } from "./components/ui/sonner";
import Homepage from "./pages/Homepage";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Groups from "./pages/Groups";
import Messages from "./pages/Messages";
import Announcements from "./pages/Announcements";
import CalendarPage from "./pages/Calendar";
import Attendance from "./pages/Attendance";
import Inventory from "./pages/Inventory";
import Finance from "./pages/Finance";
import Surveys from "./pages/Surveys";
import Documents from "./pages/Documents";
import Admin from "./pages/Admin";
import Audit from "./pages/Audit";
import Tasks from "./pages/Tasks";
import Cuotas from "./pages/Cuotas";
import SponsorPortal from "./pages/SponsorPortal";
import Automations from "./pages/Automations";
import Presupuestos from "./pages/Presupuestos";

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      // Check for SW updates
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available - dispatch custom event
              window.dispatchEvent(new CustomEvent('sw-update-available', { detail: reg }));
            }
          });
        }
      });
    }).catch(err => {
      console.log('ServiceWorker registration failed:', err);
    });
  });
}

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return (
    <NotificationProvider>
      <Layout>{children}</Layout>
      <BirthdayPopup />
    </NotificationProvider>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={<Login />} />
      
      {/* ERP private routes */}
      <Route path="/erp" element={<Navigate to="/erp/dashboard" replace />} />
      <Route path="/erp/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/erp/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/erp/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/erp/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/erp/announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />
      <Route path="/erp/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/erp/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
      <Route path="/erp/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
      <Route path="/erp/finance" element={<ProtectedRoute><Finance /></ProtectedRoute>} />
      <Route path="/erp/surveys" element={<ProtectedRoute><Surveys /></ProtectedRoute>} />
      <Route path="/erp/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
      <Route path="/erp/cuotas" element={<ProtectedRoute><Cuotas /></ProtectedRoute>} />
      <Route path="/erp/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/erp/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/erp/audit" element={<ProtectedRoute><Audit /></ProtectedRoute>} />
      <Route path="/erp/sponsor" element={<ProtectedRoute><SponsorPortal /></ProtectedRoute>} />
      <Route path="/erp/automations" element={<ProtectedRoute><Automations /></ProtectedRoute>} />
      <Route path="/erp/presupuestos" element={<ProtectedRoute><Presupuestos /></ProtectedRoute>} />
      
      {/* Legacy route redirects */}
      <Route path="/dashboard" element={<Navigate to="/erp/dashboard" replace />} />
      <Route path="/users" element={<Navigate to="/erp/users" replace />} />
      <Route path="/messages" element={<Navigate to="/erp/messages" replace />} />
      <Route path="/calendar" element={<Navigate to="/erp/calendar" replace />} />
      <Route path="/finance" element={<Navigate to="/erp/finance" replace />} />
      <Route path="/presupuestos" element={<Navigate to="/erp/presupuestos" replace />} />
      <Route path="/admin" element={<Navigate to="/erp/admin" replace />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
